import { useNavigate } from 'react-router-dom';
import { ArrowRight, LogOut, RefreshCw, ShieldAlert } from 'lucide-react';
import { defaultPanelPath, getAccessDeniedReason } from '../auth/iamAccess';
import { useAuth } from '../auth/AuthProvider';
import { Button } from './ui/button';

const SYNC_FAILURE_MESSAGE =
  'Access sync is not ready yet. This is usually temporary — try again in a moment.';

const DEFAULT_MESSAGE = 'You do not have access to this workspace.';
const DEFAULT_SUBMESSAGE = 'If you think this is a mistake, contact your March Health administrator.';

/**
 * Access-denied is never a dead end (Section I): it always offers a clear way
 * out — retry (recovers a transient IAM-sync failure), jump to a workspace the
 * user *can* reach, and sign out. No one gets stuck on this screen.
 */
export function AccessDeniedPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const reason = getAccessDeniedReason();
  const isSyncFailure = reason === 'iam_sync_failed';

  const home = defaultPanelPath();
  const hasSomewhereToGo = home !== '/access-denied';

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 text-foreground">
      <div className="w-full max-w-xl rounded-3xl border border-border bg-surface-raised p-8 text-center shadow-sm">
        <span className="mx-auto flex size-12 items-center justify-center rounded bg-status-attention-bg text-status-attention-text">
          <ShieldAlert className="size-6" />
        </span>
        <h1 className="mt-4 text-xl font-medium">
          {isSyncFailure ? 'Access sync is not ready' : 'Access denied'}
        </h1>
        <p className="mt-3 text-sm leading-6 text-text-tertiary">
          {isSyncFailure ? SYNC_FAILURE_MESSAGE : DEFAULT_MESSAGE}
        </p>
        {!isSyncFailure ? (
          <p className="mt-1 text-sm leading-6 text-text-tertiary">{DEFAULT_SUBMESSAGE}</p>
        ) : null}

        <div className="mt-6 flex flex-col items-center justify-center gap-2 sm:flex-row">
          <Button onClick={() => window.location.reload()} className="w-full sm:w-auto">
            <RefreshCw className="mr-1.5 size-4" />
            Try again
          </Button>
          {hasSomewhereToGo ? (
            <Button variant="outline" onClick={() => navigate(home)} className="w-full sm:w-auto">
              Go to my workspace
              <ArrowRight className="ml-1.5 size-4" />
            </Button>
          ) : null}
          <Button variant="ghost" onClick={() => void logout()} className="w-full sm:w-auto">
            <LogOut className="mr-1.5 size-4" />
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}
